/**
 * Frontend mirror of the server's capability model.
 *
 * Source of truth: worker/permissions.ts. The server stamps `capabilities`
 * onto every user payload, so the frontend can just consult that flag instead
 * of recomputing from role. Use `can(user, "edit_financial")` etc.
 *
 * UI checks gate visibility / disabled state. Server checks block mutation
 * (return 403). Both should agree, but if they don't, the server wins.
 */

import type { Capability, CurrentUser } from "@/api/client";

export function can(user: CurrentUser | null, cap: Capability): boolean {
  if (!user) return false;
  return user.capabilities[cap] === true;
}

export const ROLE_LABELS: Record<string, string> = {
  owner:      "Owner",
  admin:      "Admin",
  foreman:    "Foreman",
  bookkeeper: "Bookkeeper",
  viewer:     "Viewer",
  member:     "Member",
};

export const ROLE_DESCRIPTIONS: Record<string, string> = {
  owner:      "Full access including household settings and team management.",
  admin:      "Full access except team management and household settings.",
  foreman:    "Manages tasks, materials, vendors, risks, issues. No financial access.",
  bookkeeper: "Manages budgets, cost items, and vendor pricing. Operational read-only.",
  viewer:     "Read-only across the board. Good for clients or inspectors.",
  member:     "Legacy role — full access except household settings + team management.",
};
